import { motion, AnimatePresence } from 'framer-motion';
import { 
  Brain, 
  Users, 
  AlertCircle, 
  RefreshCw,
  Download,
  Filter,
  Ship,
  Calendar,
  TrendingUp,
  CheckCircle,
  Clock,
  AlertTriangle
} from 'lucide-react';
import type { SearchResult, CrewMember } from '../types';
import { CrewCard } from './CrewCard';

interface SearchResultsProps {
  results: SearchResult | null;
  isLoading: boolean;
  error: Error | null;
  onRetry?: () => void;
  onViewDetails?: (crew: CrewMember) => void;
}

function LoadingSkeleton() {
  return (
    <div className="space-y-4">
      {[1, 2, 3].map((i) => (
        <div key={i} className="card">
          <div className="flex gap-4">
            <div className="w-16 h-16 rounded-full loading-shimmer" />
            <div className="flex-1 space-y-3">
              <div className="h-5 w-1/3 loading-shimmer" />
              <div className="h-4 w-2/3 loading-shimmer" />
              <div className="flex gap-2">
                <div className="h-6 w-20 rounded-full loading-shimmer" />
                <div className="h-6 w-16 rounded-full loading-shimmer" />
                <div className="h-6 w-24 rounded-full loading-shimmer" />
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card text-center py-12"
    >
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-gray-100 
                    flex items-center justify-center">
        <Users className="w-7 h-7 text-gray-400" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">No Results Found</h3>
      <p className="text-gray-500 max-w-md mx-auto">
        No crew members match your search criteria. Try adjusting your filters 
        or use a different search query.
      </p>
    </motion.div>
  );
}

function ErrorState({ error, onRetry }: { error: Error; onRetry?: () => void }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="card text-center py-12 border-red-200"
    >
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-50 
                    flex items-center justify-center">
        <AlertCircle className="w-7 h-7 text-red-500" />
      </div>
      <h3 className="text-lg font-medium text-gray-900 mb-2">Search Failed</h3>
      <p className="text-gray-500 max-w-md mx-auto mb-4">
        {error.message || 'An error occurred while searching. Please try again.'}
      </p>
      {onRetry && (
        <button onClick={onRetry} className="btn-secondary flex items-center gap-2 mx-auto">
          <RefreshCw className="w-4 h-4" />
          Try Again
        </button>
      )}
    </motion.div>
  );
}

export function SearchResults({ 
  results, 
  isLoading, 
  error, 
  onRetry,
  onViewDetails 
}: SearchResultsProps) {
  if (isLoading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <Brain className="w-5 h-5 text-primary-600 animate-pulse" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Analyzing Crew Data...</h2>
            <p className="text-sm text-gray-500">AI is processing your request from PostgreSQL</p>
          </div>
        </div>
        <LoadingSkeleton />
      </motion.div>
    );
  }

  if (error) {
    return <ErrorState error={error} onRetry={onRetry} />;
  }

  if (!results) {
    return null;
  }

  // Handle both old format (results) and new format (recommendations)
  const crewList = results.recommendations || [];
  const summary = results.analysis || '';
  const totalResults = results.total_results || crewList.length;

  // If no crew results but has analysis/answer (e.g., statistics query), show that instead
  if (crewList.length === 0 && summary) {
    // Extract structured data if available
    const summaryData = (results as any).summary;
    const dashboardData = (results as any).dashboard;
    
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="space-y-6"
      >
        {/* Answer Header */}
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-lg">
              <Brain className="w-7 h-7 text-white" />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-2">Query Result</h3>
              <p className="text-gray-700 text-lg leading-relaxed font-medium">{summary}</p>
            </div>
          </div>
        </div>

        {/* System Overview Stats */}
        {summaryData && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              System Overview
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200">
                <div className="flex items-center gap-2 mb-2">
                  <Ship className="w-4 h-4 text-blue-600" />
                  <span className="text-sm font-medium text-gray-600">Vessels</span>
                </div>
                <p className="text-2xl font-bold text-blue-600">{summaryData.total_vessels || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-600">Active Crew</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{summaryData.total_active || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-purple-600" />
                  <span className="text-sm font-medium text-gray-600">Available</span>
                </div>
                <p className="text-2xl font-bold text-purple-600">{summaryData.total_available || 0}</p>
              </div>
              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-600">Relief Plans</span>
                </div>
                <p className="text-2xl font-bold text-orange-600">{summaryData.total_relief_plans || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Crew by Rank */}
        {summaryData?.by_rank && Object.keys(summaryData.by_rank).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              Crew Distribution by Rank
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              {Object.entries(summaryData.by_rank).map(([rank, count]) => (
                <div key={rank} className="bg-gray-50 rounded-lg p-3 border border-gray-200 text-center">
                  <p className="text-xs font-medium text-gray-500 mb-1">{rank}</p>
                  <p className="text-xl font-bold text-gray-900">{count as number}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Relief Status */}
        {dashboardData?.relief_status && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              Relief Plan Status
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-orange-50 rounded-lg p-4 border border-orange-200">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-orange-600" />
                  <span className="text-sm font-medium text-gray-600">Pending</span>
                </div>
                <p className="text-2xl font-bold text-orange-600">{dashboardData.relief_status.pending || 0}</p>
              </div>
              <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  <span className="text-sm font-medium text-gray-600">Confirmed</span>
                </div>
                <p className="text-2xl font-bold text-green-600">{dashboardData.relief_status.confirmed || 0}</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 border border-red-200">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-red-600" />
                  <span className="text-sm font-medium text-gray-600">Overdue</span>
                </div>
                <p className="text-2xl font-bold text-red-600">{dashboardData.relief_status.overdue || 0}</p>
              </div>
            </div>
          </div>
        )}

        {/* Upcoming Due Dates */}
        {dashboardData?.upcoming_due_dates && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              Upcoming Crew Changes
            </h4>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <p className="text-3xl font-bold text-blue-600 mb-1">
                  {dashboardData.upcoming_due_dates.next_7_days || 0}
                </p>
                <p className="text-sm text-gray-600">Next 7 Days</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-indigo-600 mb-1">
                  {dashboardData.upcoming_due_dates.next_14_days || 0}
                </p>
                <p className="text-sm text-gray-600">Next 14 Days</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-600 mb-1">
                  {dashboardData.upcoming_due_dates.next_30_days || 0}
                </p>
                <p className="text-sm text-gray-600">Next 30 Days</p>
              </div>
            </div>
          </div>
        )}

        {/* Active Crew by Rank */}
        {dashboardData?.active_by_rank && Object.keys(dashboardData.active_by_rank).length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-green-600" />
              Active Crew by Rank
            </h4>
            <div className="space-y-2">
              {Object.entries(dashboardData.active_by_rank)
                .sort((a, b) => (b[1] as number) - (a[1] as number))
                .map(([rank, count]) => (
                  <div key={rank} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <span className="font-medium text-gray-700">{rank}</span>
                    <div className="flex items-center gap-3">
                      <div className="w-48 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-500 h-2 rounded-full" 
                          style={{ width: `${Math.min((count as number) / Math.max(...Object.values(dashboardData.active_by_rank) as number[]) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="text-lg font-bold text-gray-900 min-w-[3rem] text-right">{count as number}</span>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
        
        {/* Relief Plans Data */}
        {(results as any).relief_plans && (results as any).relief_plans.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-orange-600" />
              Relief Plans ({(results as any).relief_plans.length})
            </h4>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Vessel</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Rank</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Outgoing Crew</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Incoming Crew</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Due Date</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {(results as any).relief_plans.slice(0, 50).map((plan: any, idx: number) => (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 font-medium text-gray-900">{plan.vessel_name || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {plan.rank || 'N/A'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{plan.outgoing_name || 'N/A'}</td>
                      <td className="px-4 py-3 text-gray-700">{plan.incoming_name || 'Not assigned'}</td>
                      <td className="px-4 py-3 text-gray-600">{plan.due_date || 'N/A'}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          plan.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                          plan.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {plan.status || 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {(results as any).relief_plans.length > 50 && (
                <p className="text-sm text-gray-500 mt-4 text-center">
                  Showing 50 of {(results as any).relief_plans.length} relief plans
                </p>
              )}
            </div>
          </div>
        )}

        {/* Vessels Data */}
        {(results as any).vessels && (results as any).vessels.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Ship className="w-5 h-5 text-blue-600" />
              Vessels ({(results as any).vessels.length})
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(results as any).vessels.slice(0, 30).map((vessel: any, idx: number) => (
                <div key={idx} className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors">
                  <div className="flex items-start justify-between mb-2">
                    <h5 className="font-semibold text-gray-900 text-sm">{vessel.name || 'Unknown'}</h5>
                    {vessel.category && (
                      <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                        {vessel.category}
                      </span>
                    )}
                  </div>
                  <div className="space-y-1 text-xs text-gray-600">
                    {vessel.teu_capacity && <p>TEU: {vessel.teu_capacity}</p>}
                    {vessel.fleet && <p>Fleet: {vessel.fleet}</p>}
                    {vessel.id && <p className="text-gray-400">ID: {vessel.id}</p>}
                  </div>
                </div>
              ))}
            </div>
            {(results as any).vessels.length > 30 && (
              <p className="text-sm text-gray-500 mt-4 text-center">
                Showing 30 of {(results as any).vessels.length} vessels
              </p>
            )}
          </div>
        )}
        
        {/* Helpful tip */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <p className="text-sm text-blue-900">
            <span className="font-semibold">💡 Tip:</span> {
              (results as any).relief_plans ? 'Use the Relief Plans page for advanced filtering and management. ' :
              (results as any).vessels ? 'Visit the Vessels page to manage your fleet. ' :
              'This query returned system information. '
            }
            Try asking about specific crew members to search personnel, or visit the{' '}
            <a href="/dashboard" className="text-blue-600 hover:underline font-semibold">Dashboard</a> for interactive visualizations.
          </p>
        </div>
      </motion.div>
    );
  }

  // Show empty state only if no results and no summary
  if (crewList.length === 0) {
    return <EmptyState />;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
            <Users className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Search Results</h2>
            <p className="text-sm text-gray-500">
              {totalResults} crew member{totalResults !== 1 ? 's' : ''} found
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button className="btn-secondary py-1.5 px-3 text-sm flex items-center gap-1.5">
            <Filter className="w-4 h-4" />
            <span className="hidden sm:inline">Sort</span>
          </button>
          <button className="btn-secondary py-1.5 px-3 text-sm flex items-center gap-1.5">
            <Download className="w-4 h-4" />
            <span className="hidden sm:inline">Export</span>
          </button>
        </div>
      </div>

      {/* Search Context */}
      {(summary || results.parsed_params) && (
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-6 space-y-3"
        >
          {/* AI Analysis */}
          {summary && (
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500 flex items-center justify-center flex-shrink-0 shadow-md">
                  <Brain className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-gray-900 mb-1 text-sm">AI Analysis</h3>
                  <p className="text-gray-700 text-sm leading-relaxed">
                    {summary}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Parsed Search Parameters */}
          {results.parsed_params && Object.keys(results.parsed_params).some(key => results.parsed_params![key as keyof typeof results.parsed_params]) && (
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Search Filters Applied:</h4>
              <div className="flex flex-wrap gap-2">
                {results.parsed_params.rank && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    <Users className="w-3 h-3 mr-1" />
                    Rank: {results.parsed_params.rank}
                  </span>
                )}
                {results.parsed_params.vessel_category && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
                    <Ship className="w-3 h-3 mr-1" />
                    Vessel: {results.parsed_params.vessel_category}
                  </span>
                )}
                {results.parsed_params.nationality && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                    🌍 Nationality: {results.parsed_params.nationality}
                  </span>
                )}
                {results.parsed_params.min_experience_months && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 border border-orange-200">
                    <TrendingUp className="w-3 h-3 mr-1" />
                    Min Experience: {results.parsed_params.min_experience_months} months
                  </span>
                )}
                {results.parsed_params.availability_days && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 border border-indigo-200">
                    <Clock className="w-3 h-3 mr-1" />
                    Available within: {results.parsed_params.availability_days} days
                  </span>
                )}
              </div>
            </div>
          )}
        </motion.div>
      )}

      {/* Results List */}
      <div className="space-y-3">
        <AnimatePresence mode="popLayout">
          {crewList.map((crew, index) => (
            <CrewCard
              key={crew.cid || crew.crew_id || index}
              crew={crew}
              index={index}
              onViewDetails={onViewDetails}
            />
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
